import { useCallback, useState } from "react";
import { useMutation, useQuery } from "urql";
import type { StakeholderAnswersQuery as StakeholderAnswersQueryResult } from "../graphql/generated/graphql";
import {
  CreateStakeholderAnswerSheetsMutation,
  ImportStakeholderAnswersMutation,
  StakeholderAnswersQuery,
} from "../graphql/stakeholderAnswers.graphql";
import { toUserFacingMessage } from "../lib/graphqlErrorMessage";

/** 回答1件。スキーマとずれないよう生成型から導出する */
export type StakeholderAnswer =
  StakeholderAnswersQueryResult["stakeholderAnswers"][number];

export interface ImportStakeholderAnswersResult {
  successCount: number;
  failureCount: number;
  results: readonly {
    answerId: string;
    success: boolean;
    errorMessage?: string | null;
  }[];
}

export interface CreateStakeholderAnswerSheetsResult {
  createdCount: number;
  failedCount: number;
  errorMessage?: string | null;
}

export function useStakeholderAnswers(termId: string | null) {
  const [result, reexecuteQuery] = useQuery({
    query: StakeholderAnswersQuery,
    variables: { termId: termId ?? "" },
    pause: !termId,
  });

  const refetch = useCallback(() => {
    reexecuteQuery({ requestPolicy: "network-only" });
  }, [reexecuteQuery]);

  return {
    stakeholderAnswers: result.data?.stakeholderAnswers ?? [],
    loading: result.fetching,
    error: result.error
      ? toUserFacingMessage(result.error, "回答の取得に失敗しました")
      : null,
    refetch,
  };
}

export function useStakeholderAnswerMutations() {
  const [, executeImport] = useMutation(ImportStakeholderAnswersMutation);
  const [, executeCreateSheets] = useMutation(
    CreateStakeholderAnswerSheetsMutation,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const importStakeholderAnswers = useCallback(
    async (
      answerIds: string[],
    ): Promise<ImportStakeholderAnswersResult | null> => {
      setLoading(true);
      setError(null);
      try {
        const response = await executeImport({ answerIds });
        if (response.error)
          throw new Error(
            toUserFacingMessage(response.error, "取り込みに失敗しました"),
          );
        return response.data?.importStakeholderAnswers ?? null;
      } catch (err) {
        setError(err instanceof Error ? err.message : "取り込みに失敗しました");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeImport],
  );

  const createStakeholderAnswerSheets = useCallback(
    async (
      termId: string,
    ): Promise<CreateStakeholderAnswerSheetsResult | null> => {
      setLoading(true);
      setError(null);
      try {
        const response = await executeCreateSheets({ termId });
        if (response.error)
          throw new Error(
            toUserFacingMessage(response.error, "シートの作成に失敗しました"),
          );
        return response.data?.createStakeholderAnswerSheets ?? null;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "シートの作成に失敗しました",
        );
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeCreateSheets],
  );

  return {
    importStakeholderAnswers,
    createStakeholderAnswerSheets,
    loading,
    error,
  };
}
